import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { claimAndPrintComande } from '../../lib/comande';
import { printReceipt, printReceiptSmart } from '../../lib/receipt';
import { PaymentDialog } from '../../components/common/PaymentDialog';
import { DiscountControl } from '../../components/common/DiscountControl';
import { ItemEditDialog } from '../../components/common/ItemEditDialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { CustomItemDialog } from '../../components/common/CustomItemDialog';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { KITCHEN_STATUS, kitchenStatusLabel, fmtEur } from '../../lib/constants';
import { cn } from '../../lib/utils';
import { useConfirm } from '../../context/ConfirmContext';
import { ArrowLeft, Plus, Minus, Trash2, Send, Receipt, ShoppingCart, Ban, FileText, CreditCard, Printer, Loader2, LayoutGrid, ChevronRight, StickyNote, Pencil } from 'lucide-react';
import { CategoryIcon } from '../../lib/categoryIcons';

export default function WaiterOrder() {
  const { t } = useTranslation();
  const { tableId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [order, setOrder] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [activeSub, setActiveSub] = useState(null);
  const [view, setView] = useState('products');
  const [optProduct, setOptProduct] = useState(null);
  const [billOpen, setBillOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [printingDoc, setPrintingDoc] = useState('');
  // Nota su un articolo del carrello (es. "senza ghiaccio").
  const [noteItem, setNoteItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [noteText, setNoteText] = useState('');

  const loadOrder = async () => {
    try { const { data } = await api.get(`/orders/by-table/${tableId}`); setOrder(data); }
    catch { setOrder({ id: null, items: [], total: 0 }); }
  };
  useEffect(() => {
    (async () => {
      const [c, p, sc] = await Promise.all([api.get('/categories'), api.get('/products?light=true&channel=tavoli'), api.get('/subcategories')]);
      setCategories(c.data); setProducts(p.data); setSubcategories(sc.data); await loadOrder();
    })();
  }, [tableId]);

  const curSubs = useMemo(() => ((activeCat && activeCat !== 'all') ? subcategories.filter((s) => s.category_id === activeCat) : []), [subcategories, activeCat]);

  const filtered = useMemo(() => {
    let list = products.filter((p) => p.available);
    if (activeCat && activeCat !== 'all') list = list.filter((p) => p.category_id === activeCat);
    if (activeSub && activeSub !== 'all') list = list.filter((p) => p.subcategory_id === activeSub);
    return list;
  }, [products, activeCat, activeSub]);

  const addProduct = async (product, opts = {}) => {
    try {
      let ord = order;
      if (!ord?.id) {
        const { data } = await api.post('/orders/open', { table_id: tableId });
        ord = data; setOrder(data);
      }
      const { data } = await api.post(`/orders/${ord.id}/items`, { product_id: product.id, qty: opts.qty || 1, variant: opts.variant || null, extras: opts.extras || [], notes: opts.notes || '' });
      setOrder(data); toast.success(t('waiter.productAdded', { name: product.name }));
    } catch (e) { toast.error(t('waiter.addProductError')); }
  };
  const clickProduct = (p) => { if (p.variants?.length || p.extras?.length) setOptProduct(p); else addProduct(p); };
  const updateQty = async (it, d) => { const nq = it.qty + d; if (nq <= 0) return removeItem(it); const { data } = await api.put(`/orders/${order.id}/items/${it.id}`, { qty: nq }); setOrder(data); };
  const openNote = (it) => { setNoteItem(it); setNoteText(it.notes || ''); };
  const saveNote = async () => {
    if (!noteItem) return;
    try {
      const { data } = await api.put(`/orders/${order.id}/items/${noteItem.id}`, { notes: noteText.trim() });
      setOrder(data); setNoteItem(null); setNoteText('');
      toast.success(t('order.noteSaved', { defaultValue: 'Nota salvata' }));
    } catch (e) { toast.error(e?.response?.data?.detail || t('waiter.error', { defaultValue: 'Errore' })); }
  };
  const removeItem = async (it, sent = false) => {
    if (sent) {
      const ok = await confirm({ title: t('waiter.voidConfirmTitle', { name: it.name }), description: t('waiter.voidConfirmDesc'), destructive: true, confirmText: t('waiter.void') });
      if (!ok) return;
    }
    try { const { data } = await api.delete(`/orders/${order.id}/items/${it.id}`); setOrder(data); if (sent) toast.success(t('waiter.itemVoided')); }
    catch (e) { toast.error(e?.response?.data?.detail || t('waiter.cannotRemove')); }
  };
  const send = async () => { try { const { data } = await api.post(`/orders/${order.id}/send`); setOrder(data); toast.success(t('waiter.sent')); claimAndPrintComande(); } catch (e) { toast.error(e?.response?.data?.detail || t('waiter.noNewItems')); } };
  const requestBill = async () => { try { const { data } = await api.post(`/orders/${order.id}/request-bill`); toast.success(data?.print_agent_enabled ? t('waiter.billSentToPrinter') : t('waiter.billRequested')); setBillOpen(false); navigate('/waiter'); } catch (e) { toast.error(e?.response?.data?.detail || t('waiter.genericError')); } };

  // Stampa un documento (resoconto non fiscale o scontrino fiscale) dal dispositivo del cameriere.
  const printDoc = async (type) => {
    if (!order?.id || isEmpty) return;
    setPrintingDoc(type);
    try {
      const { data } = await api.get(`/orders/${order.id}/receipt`, { params: { type } });
      if (data.waiter_printer && data.waiter_printer.enabled) {
        // Cameriere con stampante dedicata: stampa lì (mai sulla principale).
        await printReceiptSmart(data);
        toast.success(t('waiter.printingDoc'));
      } else if (data.print_agent_enabled) {
        toast.success(t('waiter.docSentToPrinter'));
      } else {
        await printReceipt(data);
        toast.success(t('waiter.printingDoc'));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || t('waiter.genericError'));
    } finally {
      setPrintingDoc('');
    }
  };

  // Callback dopo l'incasso: ricarica l'ordine; se il tavolo è chiuso torna all'elenco.
  const onPaid = async (data) => {
    await loadOrder();
    if (data?.order_closed) {
      setPayOpen(false);
      setBillOpen(false);
      navigate('/waiter');
    }
  };

  // Cancel a freshly opened but still empty order when leaving, freeing the table
  const goBack = async () => {
    // Dal carrello si torna alla schermata precedente (menu/prodotti), non alla selezione tavoli.
    if (view === 'cart') { setView('products'); return; }
    if (order?.id && (order.items || []).length === 0) {
      try { await api.post(`/orders/${order.id}/cancel`); } catch (e) { /* ignore */ }
    }
    navigate('/waiter');
  };

  if (!order) return <div className="p-6 text-muted-foreground">{t('waiter.loading')}</div>;
  const count = (order.items || []).reduce((s, i) => s + i.qty, 0);
  const pending = (order.items || []).reduce((s, i) => s + Math.max(0, Number(i.qty || 0) - Number(i.sent_qty || 0)), 0);
  const isEmpty = (order.items || []).length === 0;
  const activeCatName = activeCat === 'all' ? t('waiter.all') : (categories.find((c) => c.id === activeCat)?.name || '');
  const activeSubName = activeSub && activeSub !== 'all' ? (subcategories.find((s) => s.id === activeSub)?.name || '') : '';
  const countForCat = (catId) => products.filter((p) => p.available && (catId === 'all' || p.category_id === catId)).length;
  const countForSub = (subId) => products.filter((p) => p.available && p.subcategory_id === subId).length;
  const selectCategory = (id) => { setActiveCat(id); setActiveSub(null); };

  return (
    <div className="flex flex-col min-h-[calc(100dvh-56px)]">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-card">
        <Button variant="ghost" size="icon" onClick={goBack} data-testid="waiter-back-button"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="font-display font-semibold">{order.table_name}</div>
        <div className="ml-auto flex rounded-lg bg-muted p-1">
          <button onClick={() => setView('products')} className={cn('px-3 h-9 rounded-md text-sm font-medium', view === 'products' && 'bg-card shadow-sm')}>{t('waiter.menu')}</button>
          <button onClick={() => setView('cart')} className={cn('px-3 h-9 rounded-md text-sm font-medium flex items-center gap-1', view === 'cart' && 'bg-card shadow-sm')}><ShoppingCart className="h-4 w-4" /> {count}</button>
        </div>
      </div>

      {view === 'products' ? (
        <div className="flex-1 flex flex-col">
          {activeCat === null ? (
            /* Livello 1: griglia CATEGORIE a caselle grandi */
            <div className="flex-1 p-3 grid gap-3 content-start" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }} data-testid="waiter-categories-grid">
              <button
                onClick={() => selectCategory('all')}
                className="h-28 rounded-2xl border border-border bg-card active:scale-[0.98] transition-transform p-3 flex flex-col items-center justify-center gap-2 text-center hover:border-primary/40"
                data-testid="waiter-cat-all"
              >
                <LayoutGrid className="h-8 w-8 text-primary" />
                <span className="font-medium text-sm leading-tight">{t('waiter.all')}</span>
                <span className="text-xs text-muted-foreground">{countForCat('all')}</span>
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => selectCategory(c.id)}
                  className="h-28 rounded-2xl border border-border bg-card active:scale-[0.98] transition-transform p-3 flex flex-col items-center justify-center gap-2 text-center hover:border-primary/40"
                  data-testid={`waiter-cat-${c.id}`}
                >
                  <CategoryIcon iconKey={c.icon} color={c.color} className="h-8 w-8" />
                  <span className="font-medium text-sm leading-tight line-clamp-2">{c.name}</span>
                  <span className="text-xs text-muted-foreground">{countForCat(c.id)}{subcategories.some((s) => s.category_id === c.id) ? ` · ${subcategories.filter((s) => s.category_id === c.id).length} ${t('subcat.manage')}` : ''}</span>
                </button>
              ))}
              <button
                onClick={() => setCustomOpen(true)}
                className="h-28 rounded-2xl border border-dashed border-primary text-primary bg-card active:scale-[0.98] transition-transform p-3 flex flex-col items-center justify-center gap-2 text-center"
                data-testid="waiter-custom-item-button"
              >
                <Plus className="h-8 w-8" />
                <span className="font-medium text-sm leading-tight">{t('waiter.customItem')}</span>
              </button>
            </div>
          ) : (curSubs.length > 0 && activeSub === null) ? (
            /* Livello 2: griglia SOTTOCATEGORIE */
            <div className="flex-1 p-3 grid gap-3 content-start" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }} data-testid="waiter-subcategories-grid">
              <div className="col-span-2">
                <Button variant="secondary" onClick={() => setActiveCat(null)} className="w-full h-14 rounded-xl justify-start text-base font-semibold" data-testid="waiter-back-to-categories">
                  <ArrowLeft className="h-6 w-6 mr-2.5 shrink-0" />
                  {t('waiter.backToCategories', { defaultValue: 'Categorie' })}
                  <span className="ml-auto flex items-center gap-1 text-sm font-normal text-muted-foreground truncate max-w-[45%]">{activeCatName} <ChevronRight className="h-4 w-4 shrink-0" /></span>
                </Button>
              </div>
              <button onClick={() => setActiveSub('all')} className="h-28 rounded-2xl border border-border bg-card active:scale-[0.98] transition-transform p-3 flex flex-col items-center justify-center gap-2 text-center hover:border-primary/40" data-testid="waiter-sub-all">
                <LayoutGrid className="h-8 w-8 text-primary" />
                <span className="font-medium text-sm leading-tight">{t('subcat.allOf')}</span>
                <span className="text-xs text-muted-foreground">{countForCat(activeCat)}</span>
              </button>
              {curSubs.map((s) => (
                <button key={s.id} onClick={() => setActiveSub(s.id)} className="h-28 rounded-2xl border border-border bg-card active:scale-[0.98] transition-transform p-3 flex flex-col items-center justify-center gap-2 text-center hover:border-primary/40" data-testid={`waiter-sub-${s.id}`}>
                  <CategoryIcon iconKey={s.icon} color={s.color} className="h-8 w-8" />
                  <span className="font-medium text-sm leading-tight line-clamp-2">{s.name}</span>
                  <span className="text-xs text-muted-foreground">{countForSub(s.id)}</span>
                </button>
              ))}
            </div>
          ) : (
            /* Livello 3: PRODOTTI + pulsante Indietro grande */
            <>
              <div className="p-3 border-b border-border bg-card">
                <Button
                  variant="secondary"
                  onClick={() => (curSubs.length > 0 ? setActiveSub(null) : setActiveCat(null))}
                  className="w-full h-14 rounded-xl justify-start text-base font-semibold"
                  data-testid="waiter-back-to-categories"
                >
                  <ArrowLeft className="h-6 w-6 mr-2.5 shrink-0" />
                  {curSubs.length > 0 ? t('subcat.manage') : t('waiter.backToCategories', { defaultValue: 'Categorie' })}
                  <span className="ml-auto flex items-center gap-1 text-sm font-normal text-muted-foreground truncate max-w-[55%]">
                    {activeCatName}{activeSubName ? ` · ${activeSubName}` : ''} <ChevronRight className="h-4 w-4 shrink-0" />
                  </span>
                </Button>
              </div>
              <div className="flex-1 p-3 grid gap-3 content-start" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }} data-testid="waiter-products-grid">
                {filtered.length === 0 && (
                  <div className="col-span-2 text-center text-muted-foreground py-12">{t('waiter.noProductsInCat', { defaultValue: 'Nessun prodotto in questa categoria' })}</div>
                )}
                {filtered.map((p) => (
                  <button key={p.id} onClick={() => clickProduct(p)} className="h-24 rounded-xl border border-border bg-card active:scale-[0.98] transition-transform p-3 flex flex-col justify-between text-left hover:border-primary/40" data-testid={`waiter-product-${p.id}`}>
                    <span className="font-medium text-sm leading-tight line-clamp-2">{p.name}</span>
                    <span className="font-display font-semibold text-primary">{fmtEur(p.price)}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="flex-1 p-3 space-y-2">
          {(order.items || []).length === 0 && <div className="text-center text-muted-foreground py-12">{t('waiter.emptyCart')}</div>}
          {(order.items || []).map((it) => {
            const ks = KITCHEN_STATUS[it.kitchen_status] || KITCHEN_STATUS.pending;
            const sentQty = Number(it.sent_qty || 0);
            const hasPending = Number(it.qty || 0) > sentQty;
            const anySent = sentQty > 0;
            return (
              <div key={it.id} className="rounded-xl border border-border p-3">
                <div className="flex justify-between"><div className="font-medium text-sm">{it.name}{it.variant ? ` (${it.variant})` : ''}</div><div className="font-display font-semibold">{fmtEur(it.line_total)}</div></div>
                {it.notes && <div className="text-xs text-accent">{t('waiter.note')}: {it.notes}</div>}
                <div className={cn('text-xs', ks.color)}>{kitchenStatusLabel(t, it.kitchen_status)}</div>
                {anySent && hasPending && (<div className="text-[11px] text-muted-foreground">{sentQty} {t('order.sentQty', { defaultValue: 'inviati' })} · {Number(it.qty) - sentQty} {t('order.newQty', { defaultValue: 'nuovi' })}</div>)}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-1"><Button variant="outline" size="icon" className="h-9 w-9" disabled={!hasPending} onClick={() => updateQty(it, -1)}><Minus className="h-4 w-4" /></Button><span className="w-8 text-center font-medium">{it.qty}</span><Button variant="outline" size="icon" className="h-9 w-9" onClick={() => updateQty(it, 1)}><Plus className="h-4 w-4" /></Button></div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-primary" onClick={() => setEditItem(it)} data-testid={`waiter-item-edit-${it.id}`} title={t('itemEdit.title', { defaultValue: 'Modifica articolo' })}><Pencil className="h-4 w-4" /></Button>
                    {anySent
                      ? <Button variant="ghost" size="sm" className="h-9 px-2 text-destructive hover:text-destructive" data-testid="waiter-void-item" onClick={() => removeItem(it, true)}><Ban className="h-4 w-4 mr-1" /> {t('waiter.void')}</Button>
                      : <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeItem(it)}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sticky actions */}
      <div className="sticky bottom-0 border-t border-border bg-card p-3 space-y-2">
        <DiscountControl order={order} onApplied={(o) => setOrder((prev) => ({ ...prev, ...o }))} className="pb-2 border-b border-border" />
        <div className="flex items-center justify-between px-1"><span className="text-muted-foreground text-sm">{t('waiter.total')}</span><span className="font-display text-xl font-semibold">{fmtEur(order.total)}</span></div>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => setBillOpen(true)} disabled={isEmpty} className="h-12 rounded-xl" data-testid="waiter-bill-button"><Receipt className="h-4 w-4 mr-2" /> {t('waiter.bill')}</Button>
          <Button onClick={send} disabled={pending === 0} className="h-12 rounded-xl" data-testid="waiter-send-button"><Send className="h-4 w-4 mr-2" /> {t('waiter.send')} {pending > 0 ? `(${pending})` : ''}</Button>
        </div>
      </div>

      {/* Foglio "Conto e pagamento" — stampe e incasso per il cameriere */}
      <Sheet open={billOpen} onOpenChange={setBillOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl" data-testid="waiter-bill-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="flex items-center gap-2"><Receipt className="h-5 w-5" /> {t('waiter.billAndPayment')}</SheetTitle>
          </SheetHeader>
          <div className="mt-2 mb-4 flex items-center justify-between rounded-xl bg-muted px-4 py-3">
            <span className="text-sm text-muted-foreground">{t('waiter.total')}</span>
            <span className="text-2xl font-display font-semibold tabular">{fmtEur(order.total)}</span>
          </div>
          <div className="space-y-2.5">
            <Button variant="outline" className="w-full h-14 rounded-xl justify-start text-left" disabled={isEmpty || !!printingDoc} onClick={() => printDoc('resoconto')} data-testid="waiter-print-resoconto-button">
              {printingDoc === 'resoconto' ? <Loader2 className="h-5 w-5 mr-3 animate-spin shrink-0" /> : <FileText className="h-5 w-5 mr-3 shrink-0" />}
              <span className="flex flex-col">
                <span className="font-semibold">{t('waiter.printResoconto')}</span>
                <span className="text-xs text-muted-foreground font-normal">{t('waiter.printResocontoHint')}</span>
              </span>
            </Button>

            <Button className="w-full h-14 rounded-xl justify-start text-left" disabled={isEmpty} onClick={() => { setBillOpen(false); setPayOpen(true); }} data-testid="waiter-collect-button">
              <CreditCard className="h-5 w-5 mr-3 shrink-0" />
              <span className="flex flex-col">
                <span className="font-semibold">{t('waiter.collectAndReceipt')}</span>
                <span className="text-xs text-primary-foreground/80 font-normal">{t('waiter.collectAndReceiptHint')}</span>
              </span>
            </Button>

            <Button variant="ghost" className="w-full h-12 rounded-xl justify-start text-left text-muted-foreground" disabled={isEmpty} onClick={requestBill} data-testid="waiter-request-bill-button">
              <Printer className="h-5 w-5 mr-3 shrink-0" />
              <span className="font-medium">{t('waiter.requestBillCashier')}</span>
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} order={order} onPaid={onPaid} />

      <CustomItemDialog open={customOpen} onOpenChange={setCustomOpen} orderId={order.id} onAdded={(data) => { setOrder(data); setView('cart'); }} />

      <WOptions product={optProduct} onClose={() => setOptProduct(null)} onConfirm={(o) => { addProduct(optProduct, o); setOptProduct(null); }} />

      {/* Dialogo Modifica articolo (prezzo, sconto, nota) come nel profilo admin */}
      <ItemEditDialog orderId={order?.id} item={editItem} open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)} onUpdated={(data) => setOrder(data)} />

      {/* Dialogo NOTA articolo (es. "senza ghiaccio") */}
      <Dialog open={!!noteItem} onOpenChange={(o) => { if (!o) { setNoteItem(null); setNoteText(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('order.noteFor', { defaultValue: 'Nota' })}{noteItem ? `: ${noteItem.name}` : ''}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>{t('waiter.note')}</Label>
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder={t('order.notePlaceholder', { defaultValue: 'Es. senza ghiaccio, ben cotto, senza cipolla…' })}
              className="min-h-[90px]"
              data-testid="waiter-item-note-input"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">{t('order.noteHint', { defaultValue: 'La nota appare in cucina/bar e sul conto.' })}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNoteItem(null); setNoteText(''); }}>{t('common.cancel')}</Button>
            <Button onClick={saveNote} data-testid="waiter-item-note-save">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WOptions({ product, onClose, onConfirm }) {
  const { t } = useTranslation();
  const [variant, setVariant] = useState(null);
  const [extras, setExtras] = useState({});
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');
  useEffect(() => { if (product) { setVariant(product.variants?.[0]?.name || null); setExtras({}); setQty(1); setNotes(''); } }, [product]);
  if (!product) return null;
  const selExtras = (product.extras || []).filter((e) => extras[e.name]).map((e) => ({ name: e.name, price: e.price }));
  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{product.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {product.variants?.length > 0 && (
            <div><Label>{t('waiter.variant')}</Label><div className="grid grid-cols-2 gap-2 mt-1.5">{product.variants.map((v) => <button key={v.name} onClick={() => setVariant(v.name)} className={cn('h-11 rounded-lg border text-sm font-medium', variant === v.name ? 'border-primary bg-primary/5 text-primary' : 'border-border')}>{v.name}{v.price_delta ? ` (+${fmtEur(v.price_delta)})` : ''}</button>)}</div></div>
          )}
          {product.extras?.length > 0 && (
            <div><Label>{t('waiter.extras')}</Label><div className="space-y-1.5 mt-1.5">{product.extras.map((e) => <label key={e.name} className="flex items-center justify-between h-10 px-3 rounded-lg border border-border"><span className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!extras[e.name]} onChange={(ev) => setExtras((s) => ({ ...s, [e.name]: ev.target.checked }))} /> {e.name}</span><span className="text-sm text-muted-foreground">+{fmtEur(e.price)}</span></label>)}</div></div>
          )}
          <div><Label>{t('waiter.kitchenNotes')}</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" /></div>
          <div className="flex items-center gap-2"><Label className="mr-2">{t('waiter.quantity')}</Label><Button variant="outline" size="icon" onClick={() => setQty((q) => Math.max(1, q - 1))}><Minus className="h-4 w-4" /></Button><span className="w-8 text-center">{qty}</span><Button variant="outline" size="icon" onClick={() => setQty((q) => q + 1)}><Plus className="h-4 w-4" /></Button></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button><Button onClick={() => onConfirm({ variant, extras: selExtras, qty, notes })}>{t('common.add')}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
